/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-nested-ternary */
import moment from 'moment';
import PropTypes from 'prop-types';
import {
    useImperativeHandle,
    forwardRef,
    useState,
    useEffect,
} from 'react';
import { generateReasonsLabel } from 'src/utils/getReferralData';
import { yupResolver } from '@hookform/resolvers/yup';
// form
import { useForm, Controller } from 'react-hook-form';
// @mui
import { useTheme } from '@mui/material/styles';
import {
    Box,
    Typography,
    Grid,
    Card,
    FormControl,
    Divider,
    FormHelperText,
    ToggleButtonGroup,
    ToggleButton,
    Switch,
} from '@mui/material';
import useSnackbar from 'src/hooks/useCustomSnackbar';
// components
import { Iconify } from '../../../components';
import ReminderPresetSelector from '../../../components/ReminderPresetSelector';
import { ArchivedAutocomplete } from '../../../components/AutoComplete';
import { FormProvider, RHFTextField } from '../../../components/hook-form';
// constant
import {
    archiveSelection,
    followUpSelection,
    VISIBLE_CONTACT_METHODS,
    PRIMARY_ARCHIVE_REASONS,
} from '../../../constants/appointmentReason';
// hooks
// store
import {
    archivedLeadSlice,
    saveNewLeadDetail,
    updateCompleteStatus
} from '../../../redux/slices/lead';
import { updateError } from '../../../redux/slices/user';
import { useSelector, dispatch } from '../../../redux/store';
import { setLoader } from '../../../redux/slices/loader';
// validation
import { AppointmentPageSchema } from '../../../validations/validations';
// Phase 2 stale-tab RCA — preserve in-flight form across reload
import useFormStatePreservation from '../../../hooks/useFormStatePreservation';
// style
import {
    appointmentNotBooked
} from '../../../styles/DialogueTabs';
import { chipToggleButtonStyle } from '../../../styles/Common';
import {
    progressBarContainerStyle,
    progressBarTextStyle,
    progressBarTrackStyle,
    progressBarFillStyle,
    statusBadgeDoneStyle,
    statusBadgeRecommendedStyle,
    successBannerGreenStyle,
    successBannerCheckGreenStyle,
    successBannerTextStyle,
} from '../../../styles/FailedToAttendTabs';

// ---------------------------------------------------------------------------------------

const AppointmentPage = forwardRef(({ handleNext }, ref) => {
    const theme = useTheme();
    const isLight = theme.palette.mode === 'light';
    const { tableRowLead, archivedLead } = useSelector((state) => state.lead);
    const { contactMethod } = useSelector((state) => state.schema);
    const { enqueueSnackbar } = useSnackbar();
    const [activeTime, setActiveTime] = useState('custom reminder');
    const [followUp, setFollowUP] = useState(null);
    const [isCommunication, setCommunication] = useState(tableRowLead?.openCommunication || 'No');
    const [assignToState, setAssignToState] = useState([]);

    let { archiveReasons } = useSelector((state) => state.schema);

    const defaultValues = {
        reason: '',
        OtherNotes: '',
        preferredContactMethod: 'PHONE',
        reminder: '',
        reminderDateTime: null,
        addNote: '',
        archiveNotes: '',
        archived: '',
        otherNotes: '',
        customReminder: 'custom reminder',
        callReminder: ''
    };

    // ── Form-state preservation (Phase 2 stale-tab RCA, 2026-06-08) ──────────
    // Keyed by the V1 lead id so a forced reload restores this lead's
    // follow-up form (priority/method/notes/reminder DateTime).
    const fspLeadId = tableRowLead?.lead_id || tableRowLead?.id || 'none';
    const { restore: fspRestore, save: fspSave, clear: fspClear } = useFormStatePreservation(
        'v1-followup-appointment',
        fspLeadId,
    );
    const persistedValues = fspRestore();

    const methods = useForm({
        mode: 'onChange',
        resolver: yupResolver(AppointmentPageSchema),
        defaultValues: { ...defaultValues, ...(persistedValues || {}) },
    });

    const {
        control,
        setValue,
        watch,
        getValues,
        handleSubmit,
        clearErrors,
    } = methods;

    const values = watch();

    // Phase 2 — debounced persist of every RHF mutation (hook debounces).
    useEffect(() => {
        fspSave(values);
    }, [values, fspSave]);

    const [newArchiveReasons, setNewArchiveReasons] = useState([]);
    const [showMoreReasons, setShowMoreReasons] = useState(false);
    useEffect(() => {
        if (archiveReasons && archiveReasons?.length > 0) {
            if (typeof archiveReasons === 'string') {
                try { archiveReasons = JSON.parse(archiveReasons); } catch { archiveReasons = []; }
            }
            const newArchive = archiveReasons?.filter?.((value) => value !== 'UNABLE_TO_CONTACT');
            const archive = newArchive?.sort()?.map?.((value) => ({ label: value === 'NHS_WAITING_LIST' ? 'NHS waiting list' : value === 'WANT_NHS' ? 'Want NHS' : generateReasonsLabel(value), value }))
            setNewArchiveReasons(archive);
        }
    }, [archiveReasons]);

    // Follow-up chips and archive chips both come from newArchiveReasons (full Note
    // enum — schema.js no longer restricts to BE-archive-only values). The filter
    // below selects whichever values are in followUpSelection or are NHS_WAITING_LIST.

    const onSubmit = (data, status) => {
        let archiveLead = {};
        const addNotesObj = {
            note: data.reason,
            additional_notes: data.callReminder ? data.OtherNotes : data.archiveNotes,
        };
        if (data.preferredContactMethod) {
            addNotesObj.contacted_via = data.preferredContactMethod && data.preferredContactMethod;
        }
        const archiveData = {
            note: data.archiveNotes
        };
        const openCommunication = {
            id: tableRowLead?.lead_id || '',
            is_open_for_communication: isCommunication === 'Yes',
        };
        archiveLead = {
            notes: addNotesObj,
            archiveData,
            callReminder: data.callReminder,
            customReminder: data.customReminder,
            openCommunication,
            leadAssignees: assignToState?.map((el) => el?.id)
        };
        if (data.callReminder && data.customReminder !== 'no-reminder') {
            if (!data.reminderDateTime) {
                dispatch(updateError({
                    message: 'Please select tab',
                    status: false
                }));
                return;
            }
            const addReminderObj = {
                note: data.addNote,
                is_completed: false,
                group: 'FOLLOW_UP',
                assignees: assignToState?.map((el) => el?.id),
            };
            if (data.reminder) {
                // Defensive uppercase — matches Reminder enum convention (BE PR #5550 normalizes; kept as belt-and-braces).
                addReminderObj.method = data.reminder?.toUpperCase?.() || data.reminder;
            }
            if (data.reminderDateTime) {
                addReminderObj.datetime = `${moment(data.reminderDateTime).format('DD-MM-YYYY HH:mm:ss')}.0Z`;
            }
            archiveLead = {
                ...archiveLead,
                addReminderObj,

            };
        }
        dispatch(saveNewLeadDetail({
            archiveLead,
        }));
        // Phase 2 stale-tab RCA — clear persisted snapshot on successful submit
        fspClear();
        if (status?.toLowerCase?.() === 'finish') {
            dispatch(setLoader(true));
            dispatch(updateCompleteStatus(true));
            return;
        }
        handleNext(true);
    };

    useImperativeHandle(
        ref,
        () => ({
            async submit(status) {
                await handleSubmit(
                    (data) => onSubmit(data, status),
                    // onInvalid: surface validation errors instead of silently swallowing them.
                    // Also unblocks the loader/button so the user isn't left stuck.
                    () => {
                        dispatch(setLoader(false));
                        dispatch(updateError({ message: 'Please complete the required fields before continuing.', success: false }));
                    }
                )();
            },
        }),
        // Include assignToState so the submit closure sees latest selection
        [isCommunication, assignToState]
    );

    const handleChangeCommunication = () => {
        setCommunication(!isCommunication);
    };

    useEffect(() => {
        if (getValues('reason') !== '') {
            if (getValues('reason') === 'NHS_WAITING_LIST') {
                dispatch(archivedLeadSlice(null));
                setValue('archived', '');
                setValue('otherNotes', false);
                setFollowUP(false);
            }
            if (getValues('reason') === 'OTHER') {
                setFollowUP(false);
                enqueueSnackbar('Lead will be archived', {
                    variant: 'warning',
                    anchorOrigin: {
                        vertical: 'top',
                        horizontal: 'center'
                    }
                });
                dispatch(archivedLeadSlice(true));
                setValue('archived', true);
                setValue('otherNotes', true);
            }
            if (archiveSelection.includes(getValues('reason'))) {
                setFollowUP(false);
                enqueueSnackbar('Lead will be archived', {
                    variant: 'warning',
                    anchorOrigin: {
                        vertical: 'top',
                        horizontal: 'center'
                    }
                });
                dispatch(archivedLeadSlice(true));
                setValue('archived', true);
                setValue('otherNotes', false);
            }
            if (followUpSelection.includes(getValues('reason'))) {
                dispatch(archivedLeadSlice(false));
                setValue('archived', false);
                setFollowUP(true);
                setValue('otherNotes', false);
            }
            // Fallback: newly-added Note enum values (e.g. SPAM, TREATMENT_RELATED,
            // APPOINTMENT_RELATED, DUPLICATE, NOT_INTERESTED) are not in archiveSelection
            // or followUpSelection. Without this guard, followUp stays null → form stuck
            // in 1-step mode with no notes/reminder sections. Default these to the archive
            // path so the full form renders correctly.
            const reason = getValues('reason');
            if (reason && !followUpSelection.includes(reason) && !archiveSelection.includes(reason) &&
                reason !== 'NHS_WAITING_LIST' && reason !== 'OTHER') {
                setFollowUP(false);
                dispatch(archivedLeadSlice(true));
                setValue('archived', true);
                setValue('otherNotes', false);
            }
        }
        return () => {
            dispatch(archivedLeadSlice(null));
            setValue('archived', '');
        };
    }, [getValues('reason')]);

    useEffect(() => {
        setValue('customReminder', activeTime);
    }, [activeTime]);

    useEffect(() => {
        if (archivedLead === true) {
            setValue('callReminder', false);
        }
        else if (archivedLead === null) {
            setValue('callReminder', false);
        }
        else {
            setValue('callReminder', true);
        }
    }, [archivedLead]);


    // ── Progress tracking ──
    const reasonDone = values.reason !== '';
    const contactMethodDone = values.preferredContactMethod !== '';
    const reminderDone = followUp === true && (activeTime === 'no-reminder' || values.reminderDateTime !== null || (activeTime !== 'custom reminder' && activeTime !== ''));
    const notesDone = followUp === true ? values.OtherNotes !== '' : followUp === false ? values.archiveNotes !== '' : false;
    const getSteps = () => {
        if (followUp === true) return [reasonDone, contactMethodDone, reminderDone, notesDone];
        if (followUp === false) return [reasonDone, contactMethodDone, notesDone];
        return [reasonDone];
    };
    const steps = getSteps();
    const completedCount = steps.filter(Boolean).length;
    const totalSteps = steps.length;
    const progressPercent = totalSteps > 0 ? (completedCount / totalSteps) * 100 : 0;

    return (
        <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)} >
            <Card sx={{ p: 3, boxShadow: 'none' }}>
{/* Treatment name removed — already visible in header and overview panel */}
                <Box sx={progressBarContainerStyle(completedCount)}>
                    <Typography sx={progressBarTextStyle(completedCount)}>{completedCount}/{totalSteps}</Typography>
                    <Box sx={progressBarTrackStyle}>
                        <Box sx={progressBarFillStyle(progressPercent)} />
                    </Box>
                </Box>
                <Grid container spacing={1} sx={appointmentNotBooked}>
                    <Controller
                        name="reason"
                        control={control}
                        render={({ field, fieldState: { error } }) => (
                            <FormControl>
                                <Typography
                                    variant="subtitle2"
                                    sx={{ fontSize: '18px !important', fontWeight: '700', mb: 1 }}
                                >
                                    Reason {reasonDone ? <Box component="span" sx={statusBadgeDoneStyle}>Done</Box> : <Box component="span" sx={statusBadgeRecommendedStyle}>Required</Box>}
                                    {error && !field.value?.length &&
                                        <FormHelperText error sx={{ px: 2 }}>
                                            {error?.message}
                                        </FormHelperText>}
                                </Typography>
                                <Typography sx={{ fontSize: '11px', color: '#919EAB', mb: 0.75, fontWeight: 500 }}>
                                    Follow up
                                </Typography>
                                <ToggleButtonGroup
                                    color="primary"
                                    exclusive
                                    value={field.value}
                                    onChange={(e, value) => {
                                        if (value === null) {
                                            field.onChange('');
                                            return;
                                        }
                                        field.onChange(value);
                                    }}
                                    aria-label="Platform"
                                    sx={{ borderRadius: 0, border: 'none', flexWrap: 'wrap' }}
                                    error={error && !field.value?.length}
                                    defaultValue={values.preferredContactMethod}
                                >
                                    {newArchiveReasons?.filter(reason => reason.value === 'NHS_WAITING_LIST' || followUpSelection.includes(reason.value))?.map((options, index) => (
                                        <ToggleButton
                                            key={index}
                                            value={options.value}
                                            sx={chipToggleButtonStyle(isLight)}
                                        >
                                            {field.value === options.value && <Iconify icon='bi:check' width={13} height={13} sx={{ mr: 0.5 }} />}{options.label}
                                        </ToggleButton>
                                    ))}
                                </ToggleButtonGroup>
                                <Typography sx={{ fontSize: '11px', color: '#919EAB', mt: 1.25, mb: 0.75, fontWeight: 500 }}>
                                    Archive reasons
                                </Typography>
                                <ToggleButtonGroup
                                    color="primary"
                                    exclusive
                                    value={field.value}
                                    onChange={(e, value) => {
                                        if (value === null) {
                                            field.onChange('');
                                            return;
                                        }
                                        field.onChange(value);
                                    }}
                                    aria-label="Platform"
                                    sx={{ borderRadius: 0, border: 'none', flexWrap: 'wrap' }}
                                    error={error && !field.value?.length}
                                    defaultValue={values.preferredContactMethod}
                                >
                                    {newArchiveReasons?.filter(reason => reason.value !== 'NHS_WAITING_LIST' && !followUpSelection.includes(reason.value) && PRIMARY_ARCHIVE_REASONS.includes(reason.value))
                                        ?.sort((a, b) => PRIMARY_ARCHIVE_REASONS.indexOf(a.value) - PRIMARY_ARCHIVE_REASONS.indexOf(b.value))
                                        ?.map((options, index) => (
                                        <ToggleButton
                                            key={index}
                                            value={options.value}
                                            sx={chipToggleButtonStyle(isLight)}
                                        >
                                            {field.value === options.value && <Iconify icon='bi:check' width={13} height={13} sx={{ mr: 0.5 }} />}{options.label}
                                        </ToggleButton>
                                    ))}
                                </ToggleButtonGroup>
                                {!showMoreReasons && newArchiveReasons?.some(reason => reason.value !== 'NHS_WAITING_LIST' && !followUpSelection.includes(reason.value) && !PRIMARY_ARCHIVE_REASONS.includes(reason.value)) && (
                                    <Box
                                        onClick={() => setShowMoreReasons(true)}
                                        sx={{ display: 'inline-flex', alignItems: 'center', cursor: 'pointer', mt: 0.5, color: '#919EAB', fontSize: '12px', fontWeight: 500, '&:hover': { color: '#637381' } }}
                                    >
                                        <Iconify icon='eva:more-horizontal-fill' width={16} height={16} sx={{ mr: 0.5 }} /> More reasons
                                    </Box>
                                )}
                                {showMoreReasons && (
                                    <ToggleButtonGroup
                                        color="primary"
                                        exclusive
                                        value={field.value}
                                        onChange={(e, value) => {
                                            if (value === null) {
                                                field.onChange('');
                                                return;
                                            }
                                            field.onChange(value);
                                        }}
                                        aria-label="Platform"
                                        sx={{ borderRadius: 0, border: 'none', flexWrap: 'wrap', mt: 0.5 }}
                                        error={error && !field.value?.length}
                                    >
                                        {newArchiveReasons?.filter(reason => reason.value !== 'NHS_WAITING_LIST' && !followUpSelection.includes(reason.value) && !PRIMARY_ARCHIVE_REASONS.includes(reason.value))?.map((options, index) => (
                                            <ToggleButton
                                                key={index}
                                                value={options.value}
                                                sx={chipToggleButtonStyle(isLight)}
                                            >
                                                {field.value === options.value && <Iconify icon='bi:check' width={13} height={13} sx={{ mr: 0.5 }} />}{options.label}
                                            </ToggleButton>
                                        ))}
                                    </ToggleButtonGroup>
                                )}

                            </FormControl>
                        )}
                    />
                </Grid>
                {reasonDone && (
                    <Box sx={{ ...successBannerGreenStyle, mb: 2.5 }}>
                        <Box sx={successBannerCheckGreenStyle}>
                            <Iconify icon="eva:checkmark-fill" width={14} height={14} sx={{ color: '#fff' }} />
                        </Box>
                        <Typography sx={successBannerTextStyle}>
                            Reason selected: {generateReasonsLabel(values.reason)}
                        </Typography>
                    </Box>
                )}
                {followUp === false && getValues('reason') !== 'NHS_WAITING_LIST' && <Box
                    sx={{
                        display: 'block',
                        mb: 3,
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', mb: 1 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Notes</Typography>
                        {notesDone ? <Box component="span" sx={statusBadgeDoneStyle}>Done</Box> : <Box component="span" sx={statusBadgeRecommendedStyle}>Required</Box>}
                    </Box>
                    <RHFTextField
                        sx={{ width: '100%' }}
                        name='archiveNotes'
                        multiline
                        rows={4}
                        label='Archive notes'
                        placeholder='Add note here'
                    />
                </Box>
                }
                {
                    followUp !== true && <Box sx={{ pt: 3, borderTop: 'solid 1px rgba(145, 158, 171, 0.2)', '.MuiFormControl-root': { width: '100%' } }}>
                        <Controller
                            name="preferredContactMethod"
                            control={control}
                            render={({ field, fieldState: { error } }) => (
                                <FormControl>
                                    <Typography
                                        variant="subtitle2"
                                        sx={{ fontSize: '18px !important', fontWeight: '700', mb: '12px' }}
                                    >
                                        Method of contact {contactMethodDone && <Box component="span" sx={statusBadgeDoneStyle}>Done</Box>}
                                    </Typography>
                                    <ToggleButtonGroup
                                        color="primary"
                                        exclusive
                                        value={field.value}
                                        onChange={(e, value) => {
                                            if (value === null) {
                                                field.onChange('');
                                                return;
                                            }
                                            field.onChange(value);
                                        }}
                                        aria-label="Platform"
                                        sx={{ borderRadius: 0, border: 'none', flexWrap: 'wrap' }}
                                        error={error && !field.value?.length}
                                        defaultValue={values.preferredContactMethod}
                                    >
                                        {contactMethod?.filter(opt => VISIBLE_CONTACT_METHODS.includes(opt.value))?.map((options, index) => (
                                            <ToggleButton
                                                key={index}
                                                value={options.value}
                                                sx={chipToggleButtonStyle(isLight)}
                                            >
                                                {field.value === options.value && <Iconify icon='bi:check' width={13} height={13} sx={{ mr: 0.5 }} />}{options.label}
                                            </ToggleButton>
                                        ))}
                                    </ToggleButtonGroup>
                                    {error && !field.value?.length &&
                                        <FormHelperText error sx={{ px: 2, textTransform: 'capitalize' }}>
                                            {error?.message}
                                        </FormHelperText>}
                                </FormControl>
                            )}
                        />
                    </Box>
                }
                <Box
                    sx={{
                        display: 'none',
                        mb: '40px',
                    }}
                >
                    <RHFTextField sx={{ width: '100%' }} name='OtherNotes' multiline rows={4} label='Additional notes (Required)' />
                </Box>
                {followUp === true ? <>
                    <ReminderPresetSelector
                        control={control}
                        setValue={setValue}
                        clearErrors={clearErrors}
                        activeTime={activeTime}
                        onPresetChange={(val) => { setActiveTime(val); setValue('customReminder', val); }}
                        assignToState={assignToState}
                        setAssignTo={setAssignToState}
                        showStepBadges={false}
                    />
                </> : ''}
                {followUp === true && <Box sx={{ mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', mb: 1 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Notes</Typography>
                        {notesDone ? <Box component="span" sx={statusBadgeDoneStyle}>Done</Box> : <Box component="span" sx={statusBadgeRecommendedStyle}>Required</Box>}
                    </Box>
                    <RHFTextField name="OtherNotes" multiline rows={4} label="Additional notes" InputLabelProps={{ shrink: true }} placeholder='Add notes here' />
                </Box>}
                {
                    followUp === true && <Box sx={{ pt: 3, borderTop: 'solid 1px rgba(145, 158, 171, 0.2)', '.MuiFormControl-root': { width: '100%' } }}>
                        <Controller
                            name="preferredContactMethod"
                            control={control}
                            render={({ field, fieldState: { error } }) => (
                                <FormControl>
                                    <Typography
                                        variant="subtitle2"
                                        sx={{ fontSize: '18px !important', fontWeight: '700', mb: '12px' }}
                                    >
                                        Method of contact {contactMethodDone && <Box component="span" sx={statusBadgeDoneStyle}>Done</Box>}
                                    </Typography>
                                    <ToggleButtonGroup
                                        color="primary"
                                        exclusive
                                        value={field.value}
                                        onChange={(e, value) => {
                                            if (value === null) {
                                                field.onChange('');
                                                return;
                                            }
                                            field.onChange(value);
                                        }}
                                        aria-label="Platform"
                                        sx={{ borderRadius: 0, border: 'none', flexWrap: 'wrap' }}
                                        error={error && !field.value?.length}
                                        defaultValue={values.preferredContactMethod}
                                    >
                                        {contactMethod?.filter(opt => VISIBLE_CONTACT_METHODS.includes(opt.value))?.map((options, index) => (
                                            <ToggleButton
                                                key={index}
                                                value={options.value}
                                                sx={chipToggleButtonStyle(isLight)}
                                            >
                                                {field.value === options.value && <Iconify icon='bi:check' width={13} height={13} sx={{ mr: 0.5 }} />}{options.label}
                                            </ToggleButton>
                                        ))}
                                    </ToggleButtonGroup>
                                    {error && !field.value?.length &&
                                        <FormHelperText error sx={{ px: 2, textTransform: 'capitalize' }}>
                                            {error?.message}
                                        </FormHelperText>}
                                </FormControl>
                            )}
                        />
                    </Box>
                }
                {followUp === false && getValues('reason') !== 'NHS_WAITING_LIST' &&
                    <>
                        <Divider sx={{ mt: 3 }} />
                        <Box sx={{ py: 3 }} >
                            <Typography
                                variant="subtitle2"
                                sx={{ fontSize: '18px !important', fontWeight: '700', mb: 1 }}
                            >
                                Is this person open to being contacted in the future?
                            </Typography>
                            No <Switch
                                name='isCommunication'
                                checked={isCommunication}
                                onChange={handleChangeCommunication}
                                inputProps={{ 'aria-label': 'controlled' }}
                            /> Yes
                        </Box>
                        <Divider sx={{ mb: 3 }} />
                        <Box sx={{ mt: '15px' }}>
                            <Typography
                                variant="subtitle2"
                                sx={{ fontSize: '18px !important', fontWeight: '700', mb: 2 }}
                            >
                                Tags
                            </Typography>
                            <ArchivedAutocomplete type={'table'} user={tableRowLead} />
                        </Box>
                    </>}
            </Card>
        </FormProvider >
    );
});

AppointmentPage.propTypes = {
    handleNext: PropTypes.func
};

export default AppointmentPage;
